import React, { ReactElement } from 'react';
import { View, StyleSheet } from 'react-native';
import { WH, WW } from '_utils';
import { screenMargin, smallestMargin } from '_utils/sizes';
import SkeletonLoader from 'Components/SkeletonLoader';

const NoticesAllSkeletonLoader = (): ReactElement => {
    return (
        <View style={styles.container}>
            <SkeletonLoader variant="provider" style={styles.list}>
                <SkeletonItem isFirst index={0} />
                <SkeletonItem index={1} />
                <SkeletonItem index={2} />
                <SkeletonItem index={3} />
            </SkeletonLoader>
        </View>
    );
};

interface SkeletonItemProps {
    isFirst?: boolean;
    index: number;
}

const SkeletonItem = ({ isFirst, index }: SkeletonItemProps): ReactElement => {
    return (
        <SkeletonLoader
            variant="background"
            index={index}
            style={[styles.itemContainer, !isFirst && styles.itemMarginTop]}
        >
            {/* Title */}
            <SkeletonLoader variant="foreground" style={styles.titleBar} />

            {/* Timestamp */}
            <SkeletonLoader variant="foreground" style={styles.timestampBar} />

            {/* Content */}
            <View style={styles.content}>
                <SkeletonLoader variant="foreground" style={styles.contentBar} />
                <SkeletonLoader variant="foreground" style={styles.contentBar} />
                <SkeletonLoader variant="foreground" style={[styles.contentBar, styles.shortBar]} />
            </View>

            {/* User info */}
            <View style={styles.userInfo}>
                <SkeletonLoader variant="foreground" style={styles.avatar} />
                <SkeletonLoader variant="foreground" style={styles.userName} />
            </View>

            {/* Footer with tags */}
            <View style={styles.footer}>
                <View style={styles.tag}>
                    <SkeletonLoader variant="foreground" style={styles.tagIcon} />
                    <SkeletonLoader variant="foreground" style={styles.tagText} />
                </View>
                <View style={styles.tag}>
                    <SkeletonLoader variant="foreground" style={styles.tagIcon} />
                    <SkeletonLoader variant="foreground" style={styles.tagText} />
                </View>
                <SkeletonLoader variant="foreground" style={[styles.tag, styles.newTag]} />
            </View>
        </SkeletonLoader>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    list: {
        flex: 1,
        gap: smallestMargin,
        marginTop: smallestMargin,
    },
    itemContainer: {
        padding: screenMargin,
        paddingTop: screenMargin,
    },
    itemMarginTop: {
        marginTop: 0,
    },
    titleBar: {
        height: WH * 0.025,
        width: '85%',
        borderRadius: 4,
        marginBottom: WH * 0.01,
    },
    timestampBar: {
        height: WH * 0.015,
        width: '30%',
        borderRadius: 4,
        marginBottom: WH * 0.02,
    },
    content: {
        marginBottom: WH * 0.02,
    },
    contentBar: {
        height: WH * 0.015,
        borderRadius: 4,
        marginTop: WH * 0.01,
    },
    shortBar: {
        width: '60%',
    },
    userInfo: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: WH * 0.02,
        paddingTop: WH * 0.01,
    },
    avatar: {
        width: WW * 0.08,
        height: WW * 0.08,
        borderRadius: WW * 0.04,
    },
    userName: {
        height: WH * 0.015,
        width: WW * 0.2,
        borderRadius: 4,
        marginLeft: screenMargin,
    },
    footer: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: screenMargin,
        borderTopWidth: 1,
        borderTopColor: '#E5E5E5',
    },
    tag: {
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: screenMargin,
    },
    tagIcon: {
        width: WW * 0.04,
        height: WW * 0.04,
        borderRadius: 4,
    },
    tagText: {
        height: WH * 0.015,
        width: WW * 0.15,
        borderRadius: 4,
        marginLeft: screenMargin / 2,
    },
    newTag: {
        width: WW * 0.15,
        height: WH * 0.03,
        borderRadius: WW * 0.04,
        marginLeft: 'auto',
    },
});

export default NoticesAllSkeletonLoader;
